import { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useCreateGroup } from '../hooks/mutations/useCreateGroup';
import { handleItemKeyDown, addItemToList, removeItemFromList } from '../utils/inputUtils';
import { currencies } from '../constants/currencies';
import { XIcon } from 'lucide-react';
import Header from '../components/Header';
import { getUserData } from '../lib/client';

interface CreateGroupForm {
  groupName: string;
  currency: string;
  password: string;
}

export const CreateGroupPage = () => {
  const { register, handleSubmit, formState: { errors } } = useForm<CreateGroupForm>({
    defaultValues: {
      currency: 'KRW',
      password: ''
    }
  });
  const user = getUserData();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoadingRef = useRef(false);
  const [participants, setParticipants] = useState<string[]>([]);
  const [newParticipant, setNewParticipant] = useState('');
  const [isInputComposing, setIsInputComposing] = useState(false);
  const [treasurerName, setTreasurerName] = useState<string>('');
  const createGroupMutation = useCreateGroup();
  const navigate = useNavigate();

  useEffect(() => {
    if (!treasurerName) return;
    const available = Array.from(
      new Set([user?.name, ...participants].filter(Boolean).map((n) => n.trim()).filter(Boolean))
    );
    if (!available.includes(treasurerName)) {
      setTreasurerName('');
    }
  }, [participants, user?.name, treasurerName]);

  const handleAddParticipant = () => {
    const trimmedName = newParticipant.trim();
    if (trimmedName) {
      const isParticipantExist = (name: string) => participants.includes(name);
      if (addItemToList(participants, trimmedName, isParticipantExist, setParticipants)) {
        setNewParticipant('');
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    handleItemKeyDown(e, newParticipant, handleAddParticipant);
  };

  const handleCompositionStart = () => {
    setIsInputComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsInputComposing(false);
  };

  const handleRemoveParticipant = (index: number) => {
    removeItemFromList(participants, index, setParticipants);
  };

  const isCreator = (name: string) => {
    return user && name === user.name;
  };

  const onSubmit = async (data: CreateGroupForm) => {
    if (isLoadingRef.current) return;
    
    try {
      setIsSubmitting(true);
      isLoadingRef.current = true;

      if (participants.length < 1) {
        toast.error('참가자를 최소 1명 이상 추가해주세요');
        return;
      }
      
      const result = await createGroupMutation.mutateAsync({
        groupName: data.groupName,
        participants,
        userName: user.name,
        defaultCurrency: data.currency,
        password: data.password,
        treasurerName
      });
      
      navigate(`/groups/${result.data.newGroup.id}`, { state: { initialEnter: true }, replace: true });
    } catch (error) {
      console.error('Error creating group:', error);
      // 에러는 mutation의 onError에서 처리됨
    } finally {
      setIsSubmitting(false);
      isLoadingRef.current = false;
    }
  };

  return (
    <div className="container mx-auto px-4 w-full pt-20">
      <Header title="새 그룹 만들기" showBackButton onBackClick={() => navigate(-1)} />
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="mb-5 w-full">
          <label className='block'>
            그룹 이름
          </label>
          <input
            {...register("groupName", { required: "그룹 이름을 입력해주세요" })}
            id="groupNameSearch" // 아이폰 자동 완성 방지
            className="w-full"
            autoFocus
            placeholder='최대 12글자'
            maxLength={12}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setTimeout(() => {
                  document.getElementById('participantSearch')?.focus();
                }, 100);
              }
            }}
          />
          {errors.groupName && (
            <p className="text-red-500 text-sm mt-1 ml-2">{errors.groupName.message}</p>
          )}
        </div>

        <div className="mb-5 w-full">
          <label>
            송금할 때 사용할 통화
          </label>
          <div className="relative">
            <select
              {...register("currency", { required: "정산 통화를 선택해주세요" })}
              className="w-full"
            >
              {Object.entries(currencies).map(([code, { name }]) => (
                <option key={code} value={code}>
                  {`${name} (${code})`}
                </option>
              ))}
            </select>
          </div>
          <p className="text-red-500 text-sm mt-1 ml-2">정산과 송금의 기준이 되며, 추후 변경이 불가해요</p>
        </div>

        <div className="mb-6 w-full">
          <label>
            참가자 명단 (총 {participants.length + 1}명)
          </label>
          
          <div className="flex flex-wrap gap-2 mb-2 min-h-12 p-2 border border-gray-100 rounded-lg w-full">
            <div 
              className={`flex items-center rounded-lg px-3 py-1.5 bg-gray-50 text-gray-800`}
            >
              <span>{user?.name}</span>
            </div>
            {participants.map((name, index) => (
              <div 
                key={index} 
                className={`flex items-center rounded-lg px-3 py-1.5 bg-gray-50 text-gray-800`}
              >
                <span className="mr-1">{name}</span>
                {!isCreator(name) && (
                  <button
                    type="button"
                    onClick={() => handleRemoveParticipant(index)}
                    className="text-gray-800 font-bold"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          <div className="relative w-full">
            <input
              id="participantSearch" // 아이폰 자동 완성 방지
              type="text"
              placeholder="이름 입력 후 엔터 또는 탭"
              value={newParticipant}
              onChange={(e) => setNewParticipant(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onBlur={() => {
                if (!isInputComposing && newParticipant.trim()) {
                  handleAddParticipant();
                }
              }}
              className="w-full"
            />
          </div>
          
          {/* {participants.length > 0 && (
            <p className="text-gray-500 text-sm mt-1 ml-2">한 번 추가하면 삭제할 수 없으니 신중히 입력해주세요</p>
          )}   */}
        </div>

        { participants.length >= 2 && <div className="-mt-4 mb-4 w-full">
          <div className="relative">
            <select
              value={treasurerName}
              onChange={(e) => setTreasurerName(e.target.value)}
              className="w-full text-blue-500"
            >
              <option value="">총무 없음(기본값)</option>
              {Array.from(
                new Set([user?.name, ...participants].filter(Boolean).map((n) => n.trim()).filter(Boolean))
              ).map((name) => (
                <option key={name} value={name}>
                  총무: {name}
                </option>
              ))}
            </select>
          </div>
          <p className="text-gray-500 text-sm mt-1 ml-2">선택 시 총무를 통해서만 송금이 오가도록 정산돼요</p>
        </div>}

        <div className="mb-5 w-full">
          <label>
            입장 비밀번호 (선택)
          </label>
          <div className="relative">
            <input
              {...register("password", { minLength: 4, maxLength: 4 })}
              type="number"
              inputMode='numeric'
              placeholder="4자리 숫자 입력"
              pattern='[0-9]{4}'
              className="w-full"
              onChange={(e) => {
                e.preventDefault();
                e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0,4);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <p className={`text-gray-500 text-sm mt-1 ml-2 ${errors.password ? "text-red-500" : ""}`}>{errors.password ? '4자리 숫자로 입력해주세요' : '링크 유출시 안전장치로 사용돼요'}</p>
        </div>

        <div className="flex items-center justify-between w-full mt-8 mb-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-500 text-white font-semibold py-3.5 px-4 rounded-xl"
          >
            {isSubmitting ? '처리 중...' : '그룹 만들기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateGroupPage; 